// User Guide search matcher (Monday 13300893078): a line-for-line port of
// Claude Design CMP Dashboard (19) guide-search.js (stopwords, stemming,
// synonyms). The backend ranks results with the same matcher
// (cap-backend src/utilities/cmpUserGuideSearch.ts); the dashboard uses it to
// highlight words in the results and on the opened guide page.

const STOP = "how do does did i to a an the can could would should will my me we our us you your what where when why which who is are was be been of for in on at by and or with it its this that there from into as if any some all get want need please".split(" ");
const SYN: Record<string, string[]> = {
  cancel: ["cancel", "suspend", "delet", "remov", "terminat"], terminat: ["terminat", "cancel", "suspend", "delet"],
  delet: ["delet", "remov"], remov: ["remov", "delet"], reactivat: ["reactivat", "unsuspend"],
  refund: ["refund", "credit"], pay: ["pay", "paid"], paid: ["paid", "pay"],
  add: ["add", "creat"], creat: ["creat", "add"], edit: ["edit", "updat", "chang"], updat: ["updat", "edit", "chang"], chang: ["chang", "updat", "edit"],
  email: ["email", "communicat"], newsletter: ["newsletter", "content"], regist: ["regist", "ticket", "attendee"],
  login: ["login", "credential", "password"], password: ["password", "credential"], sponsor: ["sponsor"], renew: ["renew"],
};

export const stemGuideWord = (word: string): string => {
  let w = word.toLowerCase().replace(/[’']s$/, "");
  const suffixes = ["ships", "ship", "ations", "ation", "ings", "ing", "ies", "ied", "ers", "ed", "es", "s"];
  for (const s of suffixes) {
    if (w.length > s.length + 3 && w.slice(-s.length) === s) {
      w = w.slice(0, -s.length) + (s === "ies" || s === "ied" ? "y" : "");
      if (/(ed|ing)$/.test(s) && /([b-df-hj-np-tv-z])\1$/.test(w)) w = w.slice(0, -1);
      break;
    }
  }
  if (w.length > 4 && /e$/.test(w)) w = w.slice(0, -1);
  if (w === "registr") w = "regist";
  return w;
};

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
export const escapeHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
export const normText = (s: string | null | undefined) => (s || "").replace(/\s+/g, " ").trim();

export type GuideSearchGroup = { vs: string[]; re: RegExp };

export const guideSearchGroups = (query: string): GuideSearchGroup[] => {
  const out: GuideSearchGroup[] = [];
  const seen: Record<string, boolean> = {};
  (query.toLowerCase().match(/[a-z0-9][a-z0-9'’-]*/g) || []).forEach((word) => {
    if (STOP.indexOf(word) > -1) return;
    const st = stemGuideWord(word);
    if (seen[st] || st.length < 2) return;
    seen[st] = true;
    const vs = SYN[st] || [st];
    out.push({ vs, re: new RegExp(`\\b(${vs.map(escapeRe).join("|")})`, "i") });
  });
  return out;
};

/** Every variant of every word, matching to the end of the word (design allRe). */
export const guideSearchAllRe = (groups: GuideSearchGroup[]): RegExp => {
  const vs = groups.reduce<string[]>((all, group) => all.concat(group.vs), []);
  return new RegExp(`\\b(${vs.map(escapeRe).join("|")})[\\w'’-]*`, "gi");
};

const MARK_STYLE = "background:#FCE9BF;color:#0A0A0A;border-radius:2px;padding:0 1px;";

/** Result snippet: up to 200 characters around the first match, words marked #FCE9BF (design snippet()). */
export const guideSearchSnippet = (source: string, re: RegExp): string => {
  let text = source;
  const match = re.exec(text);
  re.lastIndex = 0;
  if (text.length > 200 && match) {
    const start = Math.max(0, match.index - 70);
    text = (start ? "… " : "") + text.slice(start, start + 200) + (start + 200 < text.length ? " …" : "");
  } else if (text.length > 200) text = `${text.slice(0, 200)} …`;
  return escapeHtml(text).replace(re, (word) => `<mark style="${MARK_STYLE}">${word}</mark>`);
};

export const GUIDE_MARK_STYLE = MARK_STYLE;
